import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable, computed, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../environments/environment';

export interface PublicCategory {
  id: string;
  code: string;
  name_dutch: string;
  name_english: string;
  name_french: string;
}

export interface PublicProduct {
  id: string;
  code: string;
  tax_percentage: number;
  package_size: number;
  name_dutch: string;
  description_dutch: string;
  name_english: string;
  description_english: string;
  name_french: string;
  description_french: string;
  range_1_start: number;
  range_1_end: number;
  range_2_start: number;
  range_2_end: number;
  range_3_start: number;
  range_3_end: number;
  range_4_start: number;
  range_4_end: number;
  range_5_start: number;
  range_5_end: number;
  product_image_link_1: string;
  product_image_link_2: string;
  product_image_link_3: string;
  product_image_link_4: string;
  product_image_link_5: string;
  product_video_link_1: string;
  categories: PublicCategory[];
}

type ApiResponse<T> = {
  success: boolean;
  messages?: string[];
  data: T;
};

@Injectable({ providedIn: 'root' })
export class ProductCatalogService {
  private readonly apiBaseUrl = this.resolveAPIBaseURL(environment.apiBaseUrl);
  private readonly catalogApiUrl = `${this.apiBaseUrl}/api/v1/bg-imports/public/products-catalog`;
  private readonly legacyApiUrl = `${this.apiBaseUrl}/api/v1/bg-imports/public/products`;

  public readonly loading = signal<boolean>(false);
  public readonly error = signal<string>('');
  public readonly products = signal<PublicProduct[]>([]);
  public readonly loaded = computed<boolean>(() => this.products().length > 0 && !this.loading());

  constructor(private readonly http: HttpClient) {}

  public async load(): Promise<void> {
    if (this.loading()) {
      return;
    }

    this.loading.set(true);
    this.error.set('');

    try {
      const response = await this.fetchCatalogWithFallback();
      this.products.set(Array.isArray(response?.data) ? response.data : []);
    } catch {
      this.error.set('Failed to load products. Please try again in a moment.');
    } finally {
      this.loading.set(false);
    }
  }

  public getByID(productID: string): PublicProduct | undefined {
    return this.products().find((item) => item.id === productID);
  }

  private async fetchCatalogWithFallback(): Promise<ApiResponse<PublicProduct[]>> {
    try {
      return await this.fetchFromURL(this.catalogApiUrl);
    } catch (error) {
      if (error instanceof HttpErrorResponse && error.status === 404) {
        return this.fetchFromURL(this.legacyApiUrl);
      }
      throw error;
    }
  }

  private fetchFromURL(url: string): Promise<ApiResponse<PublicProduct[]>> {
    const headers = new HttpHeaders({
      'x-request-id': this.newRequestID(),
    });
    return firstValueFrom(this.http.get<ApiResponse<PublicProduct[]>>(url, { headers }));
  }

  private newRequestID(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return `bgi-web-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }

  private resolveAPIBaseURL(url: string): string {
    return String(url || '').replace(/\/+$/, '');
  }
}
